/**
 * Utility functions for object manipulation
 */

/**
 * Removes all properties from an object that have no values
 * @param obj - The object to clean
 * @returns A new object with only properties that have values
 */
export function removeEmptyProperties<T extends Record<string, any>>(
  obj: T
): Partial<T> {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const cleaned: Partial<T> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Check if the value is not empty
    if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0) &&
      !(typeof value === 'object' && Object.keys(value).length === 0)
    ) {
      cleaned[key as keyof T] = value;
    }
  }

  return cleaned;
}

/**
 * Removes all properties from an object that have no values (recursive version)
 * @param obj - The object to clean
 * @returns A new object with only properties that have values
 */
export function removeEmptyPropertiesRecursive<T extends Record<string, any>>(
  obj: T
): Partial<T> {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const cleaned: Partial<T> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      // If it's an object (but not an array), recursively clean it
      if (typeof value === 'object' && !Array.isArray(value)) {
        const cleanedNested = removeEmptyPropertiesRecursive(value);
        if (Object.keys(cleanedNested).length > 0) {
          cleaned[key as keyof T] = cleanedNested as T[keyof T];
        }
      } else {
        cleaned[key as keyof T] = value;
      }
    }
  }

  return cleaned;
}

/**
 * Checks if a value is considered empty
 * @param value - The value to check
 * @returns True if the value is considered empty
 */
export function isEmpty(value: any): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0)
  );
}
